/**
 * Audit Trail — vista Admin read-only.
 *
 * Consume el endpoint real `GET /api/audit-log` (backend). Cada entry tiene
 * `prev_hash` + `hash` (SHA-256 chain) persistidos en la tabla `audit_log`.
 * El botón "Verificar integridad" dispara `GET /api/audit-log/verify` que
 * recomputa el chain entero y detecta tampering manual.
 *
 * Reemplaza la versión vieja basada en localStorage (`useAuditStore`); ese
 * store sigue existiendo solo para los flujos mock que todavía no fueron
 * migrados al backend.
 */

import { useState, useMemo } from 'react';
import { Search, ShieldAlert, Eye, Loader2, RefreshCw, X } from 'lucide-react';
import { useSessionStore } from '@/stores/session';
import { useAuditEntries } from '@/api/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { fmtDateTimeLong } from '@/lib/format';
import { puedeActuar } from '@/domain/rbac';
import type { AuditEntry } from '@/api/contracts/audit-log';

/** Etiquetas amigables para las acciones que registra el backend. */
const ACCION_LABEL: Record<string, string> = {
  'login.password': 'Inicio de sesión (password)',
  'login.password.failed': 'Login fallido (password)',
  'login.google': 'Inicio de sesión (Google)',
  'login.google.failed': 'Login fallido (Google)',
  'logout': 'Cierre de sesión',
  'reauth.password': 'Step-up auth',
  'gln.default_changed': 'GLN default cambiado',
  'traza.iniciada': 'Traza iniciada',
  'traza.success': 'Traza OK',
  'traza.error': 'Traza con error',
  'traza.failed': 'Traza fallida',
};

/** Categorizamos acciones para el badge de color. */
function badgeForAction(action: string): string {
  if (action.endsWith('.failed') || action === 'traza.error' || action === 'traza.failed') {
    return 'badge-red';
  }
  if (action.startsWith('login.') || action === 'logout' || action.startsWith('reauth')) {
    return 'badge-blue';
  }
  if (action.startsWith('traza.')) {
    return 'badge-green';
  }
  if (action.startsWith('gln.')) {
    return 'badge-yellow';
  }
  return 'badge-gray';
}

export function AuditPage() {
  const usuario = useSessionStore((s) => s.usuario);
  const { data, isLoading, error, refetch, isFetching } = useAuditEntries({ limit: 500 });
  const entries = useMemo(() => data?.entries ?? [], [data]);

  const [accionFiltro, setAccionFiltro] = useState<string>('all');
  const [busqueda, setBusqueda] = useState('');
  const [entrySeleccionada, setEntrySeleccionada] = useState<AuditEntry | null>(null);

  const accionesUnicas = useMemo(() => {
    const set = new Set(entries.map((e) => e.action));
    return Array.from(set).sort();
  }, [entries]);

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return entries.filter((e) => {
      if (accionFiltro !== 'all' && e.action !== accionFiltro) return false;
      if (!q) return true;
      const metadataStr = e.metadata ? JSON.stringify(e.metadata).toLowerCase() : '';
      return (
        e.action.toLowerCase().includes(q) ||
        (e.usuario?.username ?? '').toLowerCase().includes(q) ||
        (e.usuario?.nombre ?? '').toLowerCase().includes(q) ||
        (e.entityType ?? '').toLowerCase().includes(q) ||
        (e.entityId ?? '').toLowerCase().includes(q) ||
        (e.gln?.nombre ?? '').toLowerCase().includes(q) ||
        (e.ipAddress ?? '').toLowerCase().includes(q) ||
        metadataStr.includes(q)
      );
    });
  }, [entries, accionFiltro, busqueda]);

  if (!puedeActuar(usuario, 'ver_audit_trail')) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-600">
          <ShieldAlert className="mx-auto mb-2 h-8 w-8 text-red-600" />
          <p className="text-sm">No tenés permiso para ver el audit trail.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-everex-azul">Audit Trail · GAMP 5 / 21 CFR Part 11</CardTitle>
              <p className="mt-1 text-xs text-gray-600">
                Registro inmutable de acciones críticas del sistema, persistido en{' '}
                <code className="font-mono text-[10px]">audit_log</code> con hash chain SHA-256.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
                disabled={isFetching}
                className="text-xs"
              >
                <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isFetching && 'animate-spin')} />
                Refrescar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <Label className="text-xs">Acción</Label>
              <Select value={accionFiltro} onValueChange={setAccionFiltro}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {accionesUnicas.map((a) => (
                    <SelectItem key={a} value={a}>
                      {ACCION_LABEL[a] ?? a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Búsqueda libre</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  className="h-9 pl-8"
                  placeholder="Acción · usuario · entityId · IP · metadata..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Listado */}
          <div className="mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-600">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                <span className="text-sm">Cargando audit trail...</span>
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertDescription className="text-xs">
                  No se pudo cargar:{' '}
                  {error instanceof Error ? error.message : String(error)}
                </AlertDescription>
              </Alert>
            ) : filtradas.length === 0 ? (
              <p className="py-8 text-center text-xs text-gray-500">
                {entries.length === 0
                  ? 'Sin entries todavía. La primera acción auditada (login, traza, etc) la va a poblar.'
                  : 'No hay entries que coincidan con los filtros.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[11px]">Fecha</TableHead>
                      <TableHead className="text-[11px]">Acción</TableHead>
                      <TableHead className="text-[11px]">Usuario</TableHead>
                      <TableHead className="text-[11px]">GLN</TableHead>
                      <TableHead className="text-[11px]">Entidad</TableHead>
                      <TableHead className="text-[11px]">IP</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtradas.map((e) => (
                      <TableRow key={e.id} className="text-xs">
                        <TableCell className="whitespace-nowrap text-[11px] text-gray-600">
                          {e.createdAt ? fmtDateTimeLong(e.createdAt) : '—'}
                        </TableCell>
                        <TableCell>
                          <span className={cn('badge', badgeForAction(e.action))} title={e.action}>
                            {ACCION_LABEL[e.action] ?? e.action}
                          </span>
                        </TableCell>
                        <TableCell>
                          {e.usuario ? (
                            <div>
                              <div className="font-medium">{e.usuario.nombre ?? '—'}</div>
                              <div className="text-[10px] text-gray-500">@{e.usuario.username ?? '—'}</div>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-[11px]">
                          {e.gln ? (
                            <>
                              {e.gln.nombre}{' '}
                              <span className="font-mono text-[10px] text-gray-500">({e.gln.gln})</span>
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-[11px]">
                          {e.entityType ? (
                            <>
                              <span className="font-mono text-gray-600">{e.entityType}</span>
                              {e.entityId && (
                                <span className="font-mono text-gray-800"> #{e.entityId}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-[10px] text-gray-600">
                          {e.ipAddress ?? '—'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={() => setEntrySeleccionada(e)}
                            title="Ver detalle (metadata + hashes)"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="mt-2 text-[10px] text-gray-500">
                  {filtradas.length} entries
                  {entries.length !== filtradas.length && ` de ${entries.length}`}
                  {data && entries.length === data.limit && (
                    <span className="ml-2 text-gray-400">
                      · mostrando últimas {data.limit} (más antiguas no cargadas)
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <EntryDetalleDialog
        entry={entrySeleccionada}
        onClose={() => setEntrySeleccionada(null)}
      />
    </div>
  );
}

function EntryDetalleDialog({
  entry,
  onClose,
}: {
  entry: AuditEntry | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={entry !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Audit entry</span>
            {entry && (
              <span className="font-mono text-xs text-gray-600">
                #{entry.id} · {ACCION_LABEL[entry.action] ?? entry.action}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {entry && (
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-3 rounded border border-gray-100 bg-gray-50 p-3">
              <Field label="Acción">
                <span className={cn('badge', badgeForAction(entry.action))}>
                  {ACCION_LABEL[entry.action] ?? entry.action}
                </span>
              </Field>
              <Field label="Fecha">
                {entry.createdAt ? fmtDateTimeLong(entry.createdAt) : '—'}
              </Field>
              <Field label="Acción (id)">
                <code className="font-mono text-[11px]">{entry.action}</code>
              </Field>
              <Field label="Audit ID">
                <span className="font-mono">{entry.id}</span>
              </Field>
              <Field label="Usuario">
                {entry.usuario ? (
                  <>
                    {entry.usuario.nombre ?? '—'}{' '}
                    <span className="font-mono text-[10px] text-gray-500">
                      (@{entry.usuario.username ?? '—'} · id {entry.usuario.id})
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </Field>
              <Field label="GLN">
                {entry.gln ? (
                  <>
                    {entry.gln.nombre ?? '—'}{' '}
                    <span className="font-mono text-[10px] text-gray-500">
                      {entry.gln.gln ?? '—'}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </Field>
              <Field label="Entidad">
                {entry.entityType ? (
                  <>
                    <span className="font-mono">{entry.entityType}</span>
                    {entry.entityId && (
                      <>
                        {' · #'}
                        <span className="font-mono">{entry.entityId}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </Field>
              <Field label="IP">
                {entry.ipAddress ? (
                  <span className="font-mono">{entry.ipAddress}</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </Field>
              {entry.userAgent && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase text-gray-500">User-Agent</div>
                  <div className="mt-0.5 break-all font-mono text-[10px] text-gray-700">
                    {entry.userAgent}
                  </div>
                </div>
              )}
            </div>

            {entry.metadata != null && (
              <section>
                <h3 className="mb-1 text-[11px] font-semibold uppercase text-gray-600">
                  Metadata
                </h3>
                <pre className="overflow-x-auto rounded border border-gray-200 bg-white p-3 font-mono text-[10px] text-gray-700">
                  {JSON.stringify(entry.metadata, null, 2)}
                </pre>
              </section>
            )}

            <section>
              <h3 className="mb-1 text-[11px] font-semibold uppercase text-gray-600">
                Hash chain (SHA-256)
              </h3>
              <div className="space-y-2 rounded border border-gray-200 bg-white p-3">
                <div>
                  <div className="text-[10px] uppercase text-gray-500">prev_hash</div>
                  <code className="mt-0.5 block break-all font-mono text-[10px] text-gray-700">
                    {entry.prevHash ?? (
                      <span className="italic text-gray-400">(primera entry)</span>
                    )}
                  </code>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-gray-500">hash</div>
                  <code className="mt-0.5 block break-all font-mono text-[10px] text-gray-700">
                    {entry.hash}
                  </code>
                </div>
              </div>
            </section>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="mr-1 h-3 w-3" />
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-gray-500">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
